import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, ApiError, whenSessionLapses, whenDeviceLost, whenMoved } from "./api.js";
import { arrivedWithCode, enrolCodeUnused, takeEnrolCode } from "./enrolLink.js";
import { TrackingBanner } from "./components/TrackingBanner.js";
import { EnrolScreen } from "./screens/EnrolScreen.js";
import { DashboardScreen } from "./screens/DashboardScreen.js";
import { TasksScreen } from "./screens/TasksScreen.js";
import { LoginScreen } from "./screens/LoginScreen.js";
import { WorkScreen } from "./screens/WorkScreen.js";
import type { ActiveWork, Ended, Paused, Session, Employee } from "./types.js";

/**
 * How often a visible screen re-asks what is running. Slow on purpose:
 * the banner is already right in the ordinary case, and this exists for
 * the ones where it cannot be — a session closed somewhere this browser
 * cannot see.
 */
const RECHECK_MS = 60_000;

type State =
  | { kind: "checking" }
  | { kind: "enrol" }
  | { kind: "login" }
  | { kind: "work"; employee: Employee }
  | { kind: "tasks"; employee: Employee }
  | { kind: "dashboard"; employee: Employee }
  | { kind: "offline" }
  /** The app answers at another address now (MIGRATION 1.11). */
  | { kind: "moved"; to: string | undefined };

/**
 * Why a timer this screen was showing has ended, in the words of the
 * person who was running it.
 *
 * Until 2026-10-29 it listed three possible causes and said none of them
 * had happened, and the floor reported the commonest, a colleague
 * pressing Finish on a shared cart, as an unexplained logout. The server
 * now says which; a reply without it keeps the old wording rather than
 * guessing.
 */
export function endedNotice(name: string | undefined, ended: Ended | null): string {
  const what = `${name ?? "That timer"} is no longer running`;
  if (!ended) {
    return `${what}. It was stopped somewhere else, or closed automatically at the end of the day — this screen was showing an old timer.`;
  }
  if (ended.source === "AUTO_CLOSED") {
    return `${what}: it was still open at the end of the day, so it was closed automatically.`;
  }
  if (ended.source === "MANUAL" || ended.discarded) {
    return `${what}: an admin corrected it.`;
  }
  switch (ended.endReason) {
    case "COMPLETED":
      return `${what}: the task was finished on another screen, which stops everyone's timer on it.`;
    case "SWITCHED":
      return `${what}: you started something else on another screen.`;
    case "PAUSED":
      return `${what}: it was paused on another screen.`;
    default:
      return `${what}: it was stopped on another screen.`;
  }
}

export function App() {
  const [state, setState] = useState<State>({ kind: "checking" });

  // The running session lives here, not in WorkScreen, so the banner
  // stays on screen while a lead moves between the task screen and the
  // floor view. The spec's worst failure is a timer someone forgot was
  // running; it must not be possible to navigate away from the evidence.
  const [open, setOpen] = useState<Session | null>(null);
  const [active, setActive] = useState<ActiveWork | null>(null);
  const [paused, setPaused] = useState<Paused>(null);

  // What this browser last believed was running. Refs rather than state
  // because the comparison happens inside an async callback that must
  // not re-create itself every time the answer changes.
  const openRef = useRef<Session | null>(null);
  const activeRef = useRef<ActiveWork | null>(null);

  // A timer that stopped without being mentioned is this screen's
  // characteristic failure, so a re-check that finds one gone says so.
  const [closed, setClosed] = useState<string | null>(null);

  // **Only the server's answer signs anyone out.** The shift cookie is
  // httpOnly, so this browser cannot clear it. Showing the grid after a
  // failed logout made a shared tablet look signed out while the cookie
  // stayed valid, and the next reload opened the previous person's
  // screen — an admin's, when it was reproduced — for whoever held it.
  const [signOutFailed, setSignOutFailed] = useState(false);
  // Only to re-render once the unused enrolment code has been dismissed.
  const [, setEnrolNoticeGone] = useState(false);

  const forget = useCallback(() => {
    openRef.current = null;
    activeRef.current = null;
    setOpen(null);
    setActive(null);
    setPaused(null);
    setClosed(null);
    setSignOutFailed(false);
  }, []);

  // Only the latest question gets to answer. A re-check sent just before
  // Stop and answered after Stop's own refresh put a stopped timer back
  // on screen, and later announced it as "stopped somewhere else".
  const latestCheck = useRef(0);

  const refreshOpen = useCallback(
    async ({ unattended = false }: { unattended?: boolean } = {}) => {
      const mine = ++latestCheck.current;
      try {
        const was = openRef.current;
        const { session, active, paused, ended } = await api.currentSession(
          unattended ? was?.id : undefined,
        );
        if (mine !== latestCheck.current) return;
        // **Only an unattended re-check can surprise anybody.** When the
        // employee pressed Stop, the timer ending is the thing they
        // asked for, and announcing it would be noise on every stop —
        // which is how a notice stops being read.
        if (unattended && was && (!session || session.id !== was.id)) {
          setClosed(endedNotice(activeRef.current?.name, ended ?? null));
        }
        if (!unattended) setClosed(null);
        openRef.current = session;
        activeRef.current = active;
        setOpen(session);
        setActive(active);
        setPaused(paused);
      } catch {
        // A failed poll must not clear a timer the employee can see.
      }
    },
    [],
  );

  // **A phone does not reload when it comes back from the app switcher.**
  // iOS freezes a backgrounded tab and resumes the same page with React
  // state intact, so the banner carries on ticking against a startedAt
  // fetched hours ago. Nothing else here re-asks the server, so without
  // this the screen can show a timer that was closed by a switch on
  // another device, by the 03:00 cutoff, or by somebody finishing the
  // job — indefinitely, counting up, while every screen that would
  // contradict it shows nothing, because a discarded session is excluded
  // from all of them.
  //
  // Reported from the floor on 2026-09-11: a phone showing over nine
  // hours of Picking that the database had never held.
  //
  // **A layout effect, not a passive one.** Signing in and the first
  // answer about what is running can land in the same commit, and a
  // passive effect runs after paint — so a timer could be on screen with
  // nothing yet listening for the return that would correct it. In a
  // browser that gap is a frame. Under a loaded test runner it was long
  // enough to lose the event outright, which is what failed "coming back
  // to the app re-asks what is running" about one CI run in three: the
  // re-check was never slow, it never fired. Found 2026-09-24 by counting
  // listeners at the moment of dispatch.
  useLayoutEffect(() => {
    if (state.kind !== "work" && state.kind !== "tasks" && state.kind !== "dashboard") {
      return;
    }
    const recheck = () => {
      if (document.visibilityState === "visible") void refreshOpen({ unattended: true });
    };
    document.addEventListener("visibilitychange", recheck);
    // Safari can restore a page from the back-forward cache without
    // firing visibilitychange at all; pageshow is the one that covers
    // coming back to an app that was never closed.
    window.addEventListener("pageshow", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("pageshow", recheck);
    };
  }, [state.kind, refreshOpen]);

  // And a re-check while the screen is simply left open, because the
  // end-of-day cutoff closes forgotten timers at 03:00 and a phone left
  // on a bench would otherwise show a discarded session all morning.
  // Keyed on the id rather than the object so a re-fetch does not keep
  // restarting the interval.
  const openId = open?.id ?? null;
  useEffect(() => {
    if (!openId) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void refreshOpen({ unattended: true });
    }, RECHECK_MS);
    return () => clearInterval(id);
  }, [openId, refreshOpen]);

  // A 401 is two different situations. An unregistered device needs an
  // admin and a token; a lapsed shift cookie needs one tap and a PIN.
  // Telling them apart is the difference between an employee carrying on
  // and an employee fetching someone.
  const check = useCallback(async () => {
    try {
      const { employee } = await api.me();
      setState({ kind: "work", employee });
      void refreshOpen();
    } catch (err) {
      if (err instanceof ApiError && err.moved) {
        setState({ kind: "moved", to: err.movedTo });
      } else if (err instanceof ApiError && err.deviceUnregistered) {
        setState({ kind: "enrol" });
      } else if (err instanceof ApiError && err.sessionLapsed) {
        setState({ kind: "login" });
      } else {
        setState({ kind: "offline" });
      }
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // A shift session is measured in minutes and expires while somebody is
  // still looking at the screen. Until this existed only the startup
  // check noticed, so a lapsed session meant every button failing with
  // whatever generic message its own screen happened to carry, over data
  // from before it expired. One tap and a PIN is the actual answer.
  useEffect(() => {
    whenSessionLapses(() => {
      forget();
      // Not a full sign-out: the timer is still running server-side and
      // the device is still enrolled. Only this browser has stopped
      // being able to say who is holding it.
      setState((current) =>
        current.kind === "login" || current.kind === "enrol"
          ? current
          : { kind: "login" },
      );
    });

    // The other 401, and it is not the same screen. A device can stop
    // being this device mid-use — revoked, or re-keyed because a code
    // for it was redeemed elsewhere. A PIN cannot fix that; it needs a
    // token or a code, so it goes to the enrol screen. Until this
    // existed the app polled a dead cookie every thirty seconds and
    // said nothing.
    whenDeviceLost(() => {
      forget();
      setState((current) => (current.kind === "enrol" ? current : { kind: "enrol" }));
    });

    // Final, unlike the two above: nothing on this address will work
    // again, so no screen it offers — PIN pad, enrolment, "Try again" —
    // is anything but a way to waste a morning.
    whenMoved((to) => {
      forget();
      setState({ kind: "moved", to });
    });
  }, [forget]);

  const signOut = useCallback(async () => {
    setSignOutFailed(false);
    try {
      await api.logout();
    } catch (err) {
      // A lapsed cookie is already signed out, and a lost device has its
      // own handler; anything else leaves the cookie as it was.
      if (!(err instanceof ApiError && (err.sessionLapsed || err.deviceUnregistered))) {
        setSignOutFailed(true);
        return;
      }
    }
    // Signing out does not stop the timer — the session is still
    // running server-side — but this device should stop claiming to
    // know about it.
    forget();
    setState((current) => (current.kind === "enrol" ? current : { kind: "login" }));
  }, [forget]);

  const signOutNotice = signOutFailed ? (
    <p className="error app-notice" role="alert">
      <span>
        Could not sign out — the server did not answer, so this device is
        still signed in. Check the connection and try again.
      </span>
      <button type="button" className="ghost" onClick={() => void signOut()}>
        Try again
      </button>
    </p>
  ) : null;

  // Above the banner rather than inside a screen: the timer belongs to
  // the app, and so does the news that it ended.
  const timerNotice = closed ? (
    <p className="notice app-notice" role="status">
      <span>{closed}</span>
      <button type="button" className="ghost" onClick={() => setClosed(null)}>
        Dismiss
      </button>
    </p>
  ) : null;

  switch (state.kind) {
    case "checking":
      return (
        <main className="centred">
          <p className="muted">Connecting…</p>
        </main>
      );

    case "offline":
      return (
        <main className="centred">
          <h1>No connection</h1>
          <p className="muted">The server cannot be reached.</p>
          <button type="button" className="primary" onClick={() => void check()}>
            Try again
          </button>
        </main>
      );

    case "moved":
      return (
        <main className="centred">
          <h1>TaskTracker has moved</h1>
          {state.to ? (
            <p>
              <a href={state.to}>{new URL(state.to).host}</a>
            </p>
          ) : null}
          <p className="muted">
            {state.to ? "Open the new address" : "Ask an admin for the new address"} and
            add it to the home screen in place of this one. This device needs
            registering there once, with a QR code from an admin. Nothing on
            this address works any more.
          </p>
        </main>
      );

    case "enrol":
      return <EnrolScreen onEnrolled={() => void check()} />;

    case "login":
      return (
        <LoginScreen
          onSignedIn={(employee) => {
            setState({ kind: "work", employee });
            // A sign-in is not a boot. The shift cookie changed but this
            // browser never re-asked what is running, so the screen said
            // "Nothing running" over a live session — and the grid came
            // back, which is what made signing out look like a way to
            // get at it. On a shared tablet this path runs all day and
            // the boot check runs once, so this was the common case
            // rather than the edge one.
            void refreshOpen();
          }}
        />
      );

    case "work":
      return (
        <>
          {/* An enrolment link opened on a device that is already
              registered. Nothing happened, and until this said so the
              only clue was the URL — which is now stripped at boot, so
              there was no clue at all. Almost always someone showing a
              code for the device they are holding. */}
          {arrivedWithCode && enrolCodeUnused() ? (
            <p className="notice app-notice" role="status">
              <span>
                That registration link was not used — this device is
                already registered. To set up a different one, use Add a
                device on the dashboard.
              </span>
              {/* It stayed until a reload, over the floor screen of
                  everyone who signed in afterwards. Dismissing consumes
                  the code, which nothing here was going to use. */}
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  takeEnrolCode();
                  setEnrolNoticeGone(true);
                }}
              >
                Dismiss
              </button>
            </p>
          ) : null}
          {signOutNotice}
          {timerNotice}
          {open ? <TrackingBanner session={open} work={active} size="hero" /> : null}
          <WorkScreen
            employee={state.employee}
            open={open}
            active={active}
            paused={paused}
            onChanged={() => void refreshOpen()}
            onOpenTasks={() => setState({ kind: "tasks", employee: state.employee })}
            onOpenDashboard={() => setState({ kind: "dashboard", employee: state.employee })}
            onSignOut={() => void signOut()}
          />
        </>
      );

    case "tasks":
      return (
        <>
          {timerNotice}
          {open ? <TrackingBanner session={open} work={active} /> : null}
          <TasksScreen
            me={state.employee}
            onBack={() => setState({ kind: "work", employee: state.employee })}
            onJobsChanged={() => void refreshOpen()}
          />
        </>
      );

    case "dashboard":
      return (
        <>
          {timerNotice}
          {open ? <TrackingBanner session={open} work={active} /> : null}
          <DashboardScreen
            me={state.employee}
            onBack={() => setState({ kind: "work", employee: state.employee })}
          />
        </>
      );
  }
}
